import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { EnTetePage, Bouton, BandeauInfo, Onglets, SectionFormulaire, LigneFormulaire } from '../design/composants';
import PosteSelector from '../components/PosteSelector';
import { CreditCard, Briefcase, Lock, KeyRound, Copy, ShieldCheck, UserPlus } from 'lucide-react';

const CONTRATS = ['CDI','CDD','Saisonnier','Stage','Journalier'];
// RH-PAIE-2E §7-8 — libellés humains avec accents corrects, valeur technique stable en base
// (code inchangé pour ne pas casser les 131 employés déjà enregistrés avec ces valeurs).
const SITUATIONS_FAMILIALES = [
  { v: 'Celibataire', l: 'Célibataire' },
  { v: 'Marie', l: 'Marié(e)' },
  { v: 'Divorce', l: 'Divorcé(e)' },
  { v: 'Veuf', l: 'Veuf / Veuve' },
];
const ONGLETS = [
  { cle: 1, Icone: CreditCard, libelle: 'Identité' },
  { cle: 2, Icone: Briefcase, libelle: 'Poste et contrat' },
  { cle: 3, Icone: Lock, libelle: 'Accès à l\'application' },
];

// Refonte visuelle : formulaire à la manière d'Odoo (libellé à gauche, champs aérés).
const Champ = ({ id, libelle, requis, aide, large, children }) => (
  <LigneFormulaire libelle={libelle} requis={requis} aide={aide} large={large} htmlFor={id}>{children}</LigneFormulaire>
);
const Saisie = ({ id, type = 'text', value, onChange, placeholder }) => (
  <input id={id} type={type} value={value} onChange={onChange} placeholder={placeholder} className="rh-champ-saisie" />
);
const Liste = ({ id, options, value, onChange, vide = 'Sélectionner' }) => (
  <select id={id} value={value} onChange={onChange} className="rh-champ-saisie">
    <option value="">{vide}</option>
    {(Array.isArray(options) ? options : []).map((o) => <option key={o.v || o} value={o.v || o}>{o.l || o}</option>)}
  </select>
);

export default function NouvelEmployePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState('');
  const [onglet, setOnglet] = useState(1);
  const [sites, setSites] = useState([]);
  const [departements, setDepartements] = useState([]);
  const [services, setServices] = useState([]);
  const [metiers, setMetiers] = useState([]);
  // Accès initial renvoyé par le serveur : affiché une seule fois, jamais conservé.
  const [accesInitial, setAccesInitial] = useState(null);
  const [copie, setCopie] = useState(false);

  const [form, setForm] = useState({
    matricule: '', nom: '', prenom: '', date_naissance: '',
    lieu_naissance: '', genre: 'M', nationalite: 'Sénégalaise',
    situation_familiale: 'Celibataire',
    telephone: '', telephone_urgence: '', email: '', adresse: '',
    cni_numero: '', cni_expiration: '',
    site_id: '', departement_id: '', service_id: '', poste_id: '', poste: '', metier_id: '', telephone_professionnel: '',
    type_contrat: 'CDI', date_embauche: '', date_fin_contrat: '',
  });

  useEffect(() => {
    const token = localStorage.getItem('sogas_token');
    const headers = { Authorization: `Bearer ${token}` };
    const toArray = (r) => {
      const d = r.data;
      if (Array.isArray(d)) return d;
      if (d && Array.isArray(d.data)) return d.data;
      if (d && Array.isArray(d.sites)) return d.sites;
      if (d && Array.isArray(d.departements)) return d.departements;
      if (d && Array.isArray(d.services)) return d.services;
      return [];
    };
    axios.get('/api/referentiel/sites', { headers }).then(r => setSites(toArray(r)));
    axios.get('/api/referentiel/departements', { headers }).then(r => setDepartements(toArray(r)));
    axios.get('/api/referentiel/services', { headers }).then(r => setServices(toArray(r)));
    axios.get('/api/metiers', { headers, params: { actifs: '1' } }).then(r => setMetiers(r.data.metiers || [])).catch(() => setMetiers([]));
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // Helper pour lier un champ du formulaire sans recréer de composant
  const f = (k) => ({ value: form[k], onChange: e => set(k, e.target.value) });

  // RH-PAIE-2E §7 — cascade Site → Département → Service : un changement de site vide le
  // département/service devenus incompatibles, un changement de département vide le service
  // devenu incompatible. Un département/service sans rattachement (site_id/departement_id NULL,
  // référentiel incomplet) reste proposé partout — jamais masqué arbitrairement.
  // Module 0 : toutes les valeurs sont des IDENTIFIANTS (UUID) ; les entrées archivées ne sont
  // plus proposées pour une nouvelle affectation.
  const actif = (x) => x.actif !== false;
  const siteActuel = sites.find((s) => s.id === form.site_id);
  const departementsDisponibles = departements.filter(actif).filter((d) => !d.site_id || !siteActuel || d.site_id === siteActuel.id);
  const departementActuel = departements.find((d) => d.id === form.departement_id);
  const servicesDisponibles = services.filter(actif).filter((sv) => !sv.departement_id || !departementActuel || sv.departement_id === departementActuel.id);

  const choisirSite = (v) => {
    setForm((old) => ({ ...old, site_id: v, departement_id: '', service_id: '', poste_id: '', poste: '' }));
  };
  const choisirDepartement = (v) => {
    setForm((old) => ({ ...old, departement_id: v, service_id: '', poste_id: '', poste: '' }));
  };

  const soumettre = async () => {
    setErreur('');
    // Champs obligatoires (dont le site, exigé par le serveur) : l'onglet concerné s'ouvre.
    const manquants = [['matricule', 1], ['nom', 1], ['prenom', 1], ['site_id', 2], ['date_embauche', 2]].filter(([k]) => !form[k]);
    if (manquants.length) {
      setErreur('Veuillez renseigner les champs obligatoires (*) : matricule, nom, prénom, site et date d\'embauche.');
      setOnglet(manquants[0][1]);
      return;
    }
    try {
      setLoading(true);
      const token = localStorage.getItem('sogas_token');
      // Le libellé du poste est dérivé par le serveur : seul l'identifiant est transmis.
      const { poste, ...payload } = form;
      if (!payload.metier_id) delete payload.metier_id;
      const { data } = await axios.post('/api/employes', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data?.acces_initial?.mot_de_passe_provisoire) {
        setAccesInitial({ ...data.acces_initial, employeId: data.employe?.id, nom: `${data.employe?.prenom || ''} ${data.employe?.nom || ''}`.trim() });
      } else {
        navigate(data?.employe?.id ? `/employes/${data.employe.id}` : "/employes");
      }
    } catch (e) {
      setErreur(e.response?.data?.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="rh-page" style={{ maxWidth: 1080 }}>
        <EnTetePage fil={[{ libelle: 'Employés', vers: '/employes' }, { libelle: 'Nouveau salarié' }]}
          actions={<>
            <Bouton onClick={() => navigate('/employes')}>Annuler</Bouton>
            <Bouton variante="primaire" Icone={UserPlus} onClick={soumettre} disabled={loading}>{loading ? 'Création…' : 'Créer le salarié'}</Bouton>
          </>} />

        <div className="rh-formulaire">
          <h1 className="rh-fiche-nom" style={{ marginBottom: 4 }}>Nouveau salarié</h1>
          <p className="rh-sous-titre" style={{ marginBottom: 20 }}>Les informations minimales suffisent : le dossier se complète ensuite depuis la fiche.</p>

          {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}

          <Onglets libelle="Étapes de la création" actif={onglet} onChange={setOnglet} onglets={ONGLETS} />

          {onglet === 1 && (
            <>
              <SectionFormulaire titre="État civil">
                <Champ id="nv-matricule" libelle="Matricule" requis><Saisie id="nv-matricule" {...f('matricule')} placeholder="Ex. 60812" /></Champ>
                <Champ id="nv-genre" libelle="Genre" requis><Liste id="nv-genre" options={[{ v: 'M', l: 'Masculin' }, { v: 'F', l: 'Féminin' }]} {...f('genre')} /></Champ>
                <Champ id="nv-nom" libelle="Nom" requis><Saisie id="nv-nom" {...f('nom')} /></Champ>
                <Champ id="nv-prenom" libelle="Prénom" requis><Saisie id="nv-prenom" {...f('prenom')} /></Champ>
                <Champ id="nv-naissance" libelle="Date de naissance"><Saisie id="nv-naissance" type="date" {...f('date_naissance')} /></Champ>
                <Champ id="nv-lieu" libelle="Lieu de naissance"><Saisie id="nv-lieu" {...f('lieu_naissance')} /></Champ>
                <Champ id="nv-nationalite" libelle="Nationalité"><Saisie id="nv-nationalite" {...f('nationalite')} /></Champ>
                <Champ id="nv-situation" libelle="Situation familiale"><Liste id="nv-situation" options={SITUATIONS_FAMILIALES} {...f('situation_familiale')} /></Champ>
              </SectionFormulaire>

              <p className="rh-form-aide" style={{ marginTop: -20, marginBottom: 24 }}>
                Épouses et enfants s'enregistrent ensuite dans la fiche (onglet Personnel), chacun avec son certificat de mariage ou son acte de naissance.
              </p>

              <SectionFormulaire titre="Coordonnées">
                <Champ id="nv-tel-pro" libelle="Téléphone professionnel"><Saisie id="nv-tel-pro" {...f('telephone_professionnel')} /></Champ>
                <Champ id="nv-tel" libelle="Téléphone personnel"><Saisie id="nv-tel" {...f('telephone')} /></Champ>
                <Champ id="nv-tel-urgence" libelle="Téléphone d'urgence"><Saisie id="nv-tel-urgence" {...f('telephone_urgence')} /></Champ>
                <Champ id="nv-email" libelle="Email"><Saisie id="nv-email" type="email" {...f('email')} /></Champ>
                <Champ id="nv-adresse" libelle="Adresse" large><Saisie id="nv-adresse" {...f('adresse')} /></Champ>
              </SectionFormulaire>

              <SectionFormulaire titre="Pièce d'identité">
                <Champ id="nv-cni" libelle="N° CNI"><Saisie id="nv-cni" {...f('cni_numero')} /></Champ>
                <Champ id="nv-cni-exp" libelle="Expiration CNI"><Saisie id="nv-cni-exp" type="date" {...f('cni_expiration')} /></Champ>
              </SectionFormulaire>
            </>
          )}

          {onglet === 2 && (
            <>
              <SectionFormulaire titre="Affectation">
                <Champ id="nv-site" libelle="Site" requis><Liste id="nv-site" options={sites.filter(actif).map((x) => ({ v: x.id, l: x.code ? `${x.code} — ${x.nom}` : x.nom }))} value={form.site_id} onChange={(e) => choisirSite(e.target.value)} /></Champ>
                <Champ id="nv-dep" libelle="Département"><Liste id="nv-dep" options={departementsDisponibles.map((d) => ({ v: d.id, l: d.nom }))} value={form.departement_id} onChange={(e) => choisirDepartement(e.target.value)} /></Champ>
                <Champ id="nv-service" libelle="Service"><Liste id="nv-service" options={servicesDisponibles.map((x) => ({ v: x.id, l: x.nom }))} {...f('service_id')} /></Champ>
                <Champ id="nv-metier" libelle="Métier"><Liste id="nv-metier" options={metiers.map((m) => ({ v: m.id, l: m.libelle }))} {...f('metier_id')} /></Champ>
                <Champ libelle="Poste de responsabilité" aide="Facultatif : seulement pour un poste de l'organigramme." large>
                  <PosteSelector
                    valueId={form.poste_id} valueLabel={form.poste}
                    departementId={departementActuel?.id} siteId={siteActuel?.id}
                    onChange={(p) => setForm((old) => ({ ...old, poste_id: p?.id || '', poste: p?.intitule || '' }))}
                  />
                </Champ>
              </SectionFormulaire>

              <SectionFormulaire titre="Contrat">
                <Champ id="nv-contrat" libelle="Type de contrat" requis><Liste id="nv-contrat" options={CONTRATS.map((v) => ({ v, l: v }))} {...f('type_contrat')} /></Champ>
                <Champ id="nv-embauche" libelle="Date d'embauche" requis><Saisie id="nv-embauche" type="date" {...f('date_embauche')} /></Champ>
                {form.type_contrat === 'CDI' ? (
                  <Champ libelle="Fin de contrat"><span className="rh-champ-vide">Sans objet pour un CDI</span></Champ>
                ) : (
                  <Champ id="nv-fin" libelle="Fin de contrat"><Saisie id="nv-fin" type="date" {...f('date_fin_contrat')} /></Champ>
                )}
              </SectionFormulaire>
              <p className="rh-form-aide" style={{ marginTop: -16 }}>
                Le salaire de base et la catégorie professionnelle se gèrent depuis l'onglet « Paie » de la fiche, une fois celle-ci créée
                (historisation avec date d'effet, famille professionnelle et motif).
              </p>
            </>
          )}

          {onglet === 3 && (
            <SectionFormulaire titre="Accès à l'application">
              <div style={{ gridColumn: '1 / -1', fontSize: 14, color: 'var(--sg-texte-2)', lineHeight: 1.6 }}>
                <p style={{ margin: '0 0 10px' }}>
                  Le compte est créé avec le rôle <strong style={{ fontWeight: 500 }}>Salarié</strong>. Les droits supplémentaires
                  s'attribuent ensuite depuis « Droits et accès », par une personne habilitée, avec un motif.
                </p>
                <p style={{ margin: 0 }}>
                  Un mot de passe provisoire est généré par le système et affiché une seule fois après la
                  création. Le salarié devra choisir son propre mot de passe à sa première connexion.
                </p>
              </div>
            </SectionFormulaire>
          )}

          <div className="rh-form-pied">
            {onglet > 1 && <Bouton variante="discret" onClick={() => setOnglet((o) => o - 1)}>Étape précédente</Bouton>}
            {onglet < 3 && <Bouton onClick={() => setOnglet((o) => o + 1)}>Étape suivante</Bouton>}
            <Bouton variante="primaire" Icone={UserPlus} onClick={soumettre} disabled={loading}>{loading ? 'Création…' : 'Créer le salarié'}</Bouton>
          </div>
        </div>
      </div>
      {accesInitial && (
        <div role="dialog" aria-modal="true" aria-labelledby="acces-initial-titre"
          style={{ position: 'fixed', inset: 0, background: 'rgba(33,37,41,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div className="rh-carte" style={{ maxWidth: 480, width: '100%', boxShadow: 'var(--sg-ombre-haute)', marginBottom: 0, fontFamily: 'var(--sg-police)' }}>
            <h2 id="acces-initial-titre" className="rh-carte-titre" style={{ marginBottom: 6 }}>
              <ShieldCheck size={18} aria-hidden="true" /> Compte créé
            </h2>
            <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--sg-texte-2)' }}>
              {accesInitial.nom || 'Le salarié'} peut se connecter avec les informations ci-dessous.
            </p>
            <div style={{ background: 'var(--sg-surface-2)', border: '1px solid var(--sg-bord)', borderRadius: 6, padding: '12px 14px', fontSize: 14, marginBottom: 12 }}>
              <div style={{ marginBottom: 8 }}><span style={{ color: 'var(--sg-texte-3)' }}>Identifiant (matricule) :</span> <strong style={{ fontWeight: 500 }}>{accesInitial.identifiant}</strong></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <KeyRound size={14} color="var(--sg-texte-3)" aria-hidden="true" />
                <span style={{ color: 'var(--sg-texte-3)' }}>Mot de passe provisoire :</span>
                <code style={{ fontSize: 15, fontWeight: 600, letterSpacing: 1 }}>{accesInitial.mot_de_passe_provisoire}</code>
                <Bouton petit Icone={Copy} onClick={() => { navigator.clipboard?.writeText(accesInitial.mot_de_passe_provisoire).then(() => setCopie(true)).catch(() => {}); }}>
                  {copie ? 'Copié' : 'Copier'}
                </Bouton>
              </div>
            </div>
            <BandeauInfo ton="alerte">Ce mot de passe ne sera plus affiché. Remettez-le au salarié de façon confidentielle ; il devra le changer à sa première connexion. En cas de perte : « Réinitialiser le mot de passe ».</BandeauInfo>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Bouton variante="primaire" onClick={() => { const id = accesInitial.employeId; setAccesInitial(null); navigate(id ? `/employes/${id}` : "/employes"); }}>
                Ouvrir la fiche du salarié
              </Bouton>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
